import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Loader } from '../../../common/components/Loader';
import { config } from '../../../config';
import { ROUTE_LOGIN } from '../../../constants/routes';

type LoginError =
  | { kind: 'provisioning' }
  | { kind: 'missing-code' }
  | { kind: 'generic'; message: string };

function classifyError(err: unknown): LoginError {
  const message = err instanceof Error ? err.message : String(err);

  if (message.includes('403')) {
    return { kind: 'provisioning' };
  }

  if (/missing authorization code/i.test(message)) {
    return { kind: 'missing-code' };
  }

  return { kind: 'generic', message };
}

export const LoginSuccessPage: React.FC = () => {
  const { completeLogin } = useAuth();
  const navigate = useNavigate();
  const [loginError, setLoginError] = useState<LoginError | null>(null);
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    const params = new URLSearchParams(window.location.search);
    completeLogin(params)
      .then(() => navigate(config.defaultLandingPath, { replace: true }))
      .catch(err => {
        console.error('[auth] login-success callback failed', err);
        setLoginError(classifyError(err));
      });
  }, [completeLogin, navigate]);

  if (loginError) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-2 bg-canvas text-text-primary">
        {loginError.kind === 'provisioning' && (
          <>
            <p className="text-sm text-error">
              This Google account is not provisioned. Contact your administrator.
            </p>
            <a href={ROUTE_LOGIN} className="text-sm text-text-link underline">
              Back to login
            </a>
          </>
        )}
        {loginError.kind === 'missing-code' && (
          <>
            <p className="text-sm text-error">Login failed — missing authorization code.</p>
            <a href={ROUTE_LOGIN} className="text-sm text-text-link underline">
              Back to login
            </a>
          </>
        )}
        {loginError.kind === 'generic' && (
          <>
            <p className="text-sm text-error">{loginError.message}</p>
            <p className="text-sm text-text-secondary">
              Please try again or contact support if the issue persists.
            </p>
            <a href={ROUTE_LOGIN} className="text-sm text-text-link underline">
              Back to login
            </a>
          </>
        )}
      </div>
    );
  }

  return <Loader label="Signing you in…" />;
};
